import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { FixedPriceMutationDto, FixedPriceOverviewDto } from '../../../config/registrationConfig'
import ModifyRegistrationRow, {
    ModifyRegistrationColumnProps,
    ModifyRegistrationColumns,
    ModifyRegistrationRowProps,
    useRegistrationRowStyles,
} from '../Registration/ModifyRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { FixedPriceListProps, getEffectiveFixedPriceRate } from './FixedPriceList'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import { Textarea, TextareaProps } from '@fluentui/react-components'
import FixedPriceTypeCombobox, { FixedPriceTypeComboboxProps } from '../../Combobox/FixedPriceTypeCombobox'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'
import useVatTypes from '../../../hooks/useVatTypes'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import { PricePrecedence } from '../../../config/finance/financeConfig'
import useCurrency from '../../../hooks/useCurrency'
import { usePrevious } from '@fluentui/react-hooks'
import useFixedPriceType from '../../../hooks/useFixedPriceType'

type Props = ModifyRegistrationRowProps<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps>
type ColumnProps = ModifyRegistrationColumnProps<FixedPriceListProps>

function Columns(props: ColumnProps): ReactElement {
    const registrationStyles = useRegistrationRowStyles()
    const { values, setFieldValue, setValues } = useFormikContext<FixedPriceMutationDto>()
    const prevValues = usePrevious(values)

    const { fixedPriceTypes, getFixedPriceType } = useFixedPriceTypes()
    const { vatTypes, getVatType } = useVatTypes()

    const { currency } = useCurrency(values.currency)
    const [fixedPriceType] = useFixedPriceType(values.type)

    useEffect(() => {
        const newFixedPriceType = getFixedPriceType(values.type)
        const newVatType = getVatType(values.vatType)

        if ((!newFixedPriceType || !newFixedPriceType?.includingVat) && values.rate && values.rate !== prevValues?.rate) {
            setFieldValue(
                'rateTotal',
                getEffectiveFixedPriceRate(values.rate, values.rateTotal, newFixedPriceType, newVatType, PricePrecedence.Total),
                false,
            )
        }
    }, [values.rate])

    useEffect(() => {
        const newFixedPriceType = getFixedPriceType(values.type)
        const newVatType = getVatType(values.vatType)

        if (newFixedPriceType?.includingVat && values.rateTotal && values.rateTotal !== prevValues?.rateTotal) {
            setFieldValue(
                'rate',
                getEffectiveFixedPriceRate(values.rate, values.rateTotal, newFixedPriceType, newVatType, PricePrecedence.Subtotal),
                false,
            )
        }
    }, [values.rateTotal])

    //When there is no long_description set use the name as name and the description as description.
    //When there is a long_description set use the description as name and the long_description as description.
    const useName = useMemo<boolean>(() => fixedPriceTypes.filter(type => !!type.longDescription).length === 0, [fixedPriceTypes])

    switch (props.column.key) {
        case 'type':
            return (
                fixedPriceTypes.length > 0 && (
                    <FormikComboboxField<FixedPriceMutationDto, FixedPriceTypeComboboxProps>
                        fieldName={'type'}
                        component={FixedPriceTypeCombobox}
                        size={'small'}
                        componentProps={{
                            selectedOptions: [values.type],
                            currency: values.currency,
                            date: values.date,
                            onOptionSelect: (_, data) => {
                                const newFixedPriceType = getFixedPriceType(data.selectedOptions?.[0])

                                setValues(
                                    oldValues => ({
                                        ...oldValues,
                                        type: data.selectedOptions?.[0],
                                        vatType: newFixedPriceType?.vatType || vatTypes[0].id,
                                        description: newFixedPriceType?.longDescription ?? newFixedPriceType?.description,
                                        rate: newFixedPriceType?.includingVat ? undefined : newFixedPriceType?.rate,
                                        rateTotal: newFixedPriceType?.includingVat ? newFixedPriceType?.rate : undefined,
                                    }),
                                    false,
                                )
                            },
                            getEntityLabel: entity => (useName ? entity.name : entity.description),
                            className: useName ? registrationStyles.type : registrationStyles.typeWide,
                            input: {
                                className: useName ? registrationStyles.typeInput : registrationStyles.typeWideInput,
                            },
                        }}
                    />
                )
            )
        case 'description':
            return (
                <FormikField<TextareaProps, FixedPriceMutationDto>
                    fieldName={'description'}
                    component={Textarea}
                    componentProps={{
                        size: 'small',
                        className: registrationStyles.description,
                        resize: 'vertical',
                    }}
                />
            )
        case 'rate':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) && (
                    <>
                        {fixedPriceType?.includingVat ? (
                            <MoneyText currency={currency} amount={values.rate} align="right" />
                        ) : (
                            <FormikField<MoneyInputProps, FixedPriceMutationDto>
                                fieldName={'rate'}
                                component={MoneyInput}
                                componentProps={{
                                    size: 'small',
                                    currency: values.currency,
                                    allowNegative: true,
                                    onCommit: (_, value: string) => setFieldValue('rate', value),
                                    className: registrationStyles.rate,
                                    input: { className: registrationStyles.rateInput },
                                }}
                            />
                        )}
                    </>
                )
            )
        case 'vat':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) && (
                    <FormikComboboxField<FixedPriceMutationDto, VatTypeComboboxProps>
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        componentProps={{
                            size: 'small',
                            selectedOptions: [values.vatType],
                            onOptionSelect: (_, data) => setFieldValue('vatType', data.selectedOptions?.[0]),
                            className: registrationStyles.vat,
                            input: { className: registrationStyles.vatInput },
                        }}
                    />
                )
            )
        case 'total':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) && (
                    <>
                        {fixedPriceType?.includingVat ? (
                            <FormikField<MoneyInputProps, FixedPriceMutationDto>
                                fieldName={'rateTotal'}
                                component={MoneyInput}
                                componentProps={{
                                    size: 'small',
                                    currency: values.currency,
                                    allowNegative: true,
                                    onCommit: (_, value: string) => setFieldValue('rateTotal', value),
                                    className: registrationStyles.rate,
                                    input: { className: registrationStyles.rateInput },
                                }}
                            />
                        ) : (
                            <MoneyText currency={currency} amount={values.rateTotal} align="right" />
                        )}
                    </>
                )
            )
    }
    return <ModifyRegistrationColumns<FixedPriceMutationDto, FixedPriceListProps> {...props} />
}

export default function ModifyFixedPriceRow(props: Props): ReactElement {
    return <ModifyRegistrationRow<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps> {...props} columns={Columns} />
}
